import { AxiosError, AxiosResponse } from 'axios';
import { DateTool } from '@/util';
import { Product } from './Product';
import { ProductValidationResult } from './ProductValidationResult';
import { ValidationResult } from './ValidationResult';
import VersionedResource from '../VersionedResource';
import { QualRewardGroup } from './levelrewards';
import {
  IQualifier, LinkedAccountQualifier, IbLoginQualifier, EStatementQualifier, AchQualifier,
  PosQualifier, CreditCardQualifier, CreditOrDebitCardQualifier, MinimumBalanceQualifier,
  LoanQualifier,
  VafaOptOutQualifier,
} from './levelrewards/qualifiers';

import {
  IReward, AtmReward, InterestReward, CashBackReward, EatsReward, StreamingReward, FeeReductionReward,
  AchAggregateFeeReductionReward, PerAchTrxTypeFeeReductionReward, PerPosTrxTypeFeeReductionReward,
  FlexReward, TunesGrantReward, IbLoginFeeReductionReward, EstatementFeeReductionReward,
  WaiveServiceFeeReward, MinimumBalanceFeeReductionReward,
} from './levelrewards/rewards';

export default class ProductResource extends VersionedResource {
  public async getCurrentForConfigVersion(fiId: string): Promise<Product[]> {
    const url = `/api/rcx/config/${fiId}/product`;
    const productsMap: Map<number, Product[]> = await this.getAllMappedForConfigVersion(fiId);
    const mostRecentProducts: Product[] = [];
    productsMap.forEach((products: Product[], key: number) => {
      const mostRecentProduct: Product | null = ProductResource.findMostRecent(products);
      if (mostRecentProduct) {
        mostRecentProducts.push(mostRecentProduct);
      }
    });

    mostRecentProducts.sort((product1, product2) => {
      if (product1.productMapping === null || product2.productMapping === null) {
        return 0;
      }
      if (product1.productMapping.fiProductName === product2.productMapping.fiProductName) {
        return 0;
      }
      if (product1.productMapping.fiProductName > product2.productMapping.fiProductName) {
        return 1;
      }
      return -1;
    });

    mostRecentProducts.forEach((product: Product) => {
      ProductResource.convertProductQualsAndRewardsToClasses(product);
    });

    return mostRecentProducts;
  }

  public async getProductValidationsForCurrentVersion(fiId: string): Promise<ProductValidationResult[]> {
    const currentProducts: Product[] = await this.getCurrentForConfigVersion(fiId);
    const allVersionsOfProductValidations: ProductValidationResult[] = await this.validateProducts(fiId);
    const currentProductIds: Set<number> = new Set<number>();
    currentProducts.forEach((product) => {
      const productId = product.id;
      if (productId) {
        currentProductIds.add(productId);
      }
    });

    const currentProductValidations: ProductValidationResult[] = [];
    allVersionsOfProductValidations.forEach((validation) => {
      if (currentProductIds.has(validation.productId)) {
        currentProductValidations.push(validation);
      }
    });

    return currentProductValidations;
  }

  public async getAllMappedForConfigVersion(fiId: string): Promise<Map<number, Product[]>> {
    const url = `/api/rcx/config/${fiId}/product`;

    try {
      const response: AxiosResponse = await this.apiClient.get(url);
      const allProducts: Product[] = response.data.data;
      const productMap: Map<number, Product[]> = new Map();
      allProducts.forEach((product: Product) => {
        ProductResource.convertProductQualsAndRewardsToClasses(product);
        const mapId = product.productMapping.externalProductMappingId;
        if (mapId === null) {
          // intentionally skips products whithout assigned product mappings
        } else if (!productMap.has(mapId)) {
          productMap.set(mapId, [product]);
        } else {
          const products = productMap.get(mapId);
          if (products) {
            products.push(product);
          }
        }
      });

      return productMap;
    } catch (error) {
      // Will 404 if Product has not been set up yet, so return empty Product
      if (error.response.status !== 404) {
        throw error;
      } else {
        return new Map();
      }
    }
  }

  public async save(fiId: string, product: Product): Promise<Product> {
    const url = `/api/rcx/config/${fiId}/product`;
    let response: AxiosResponse;

    if (product.id) {
      response = await this.apiClient.put(`${url}/${product.id}`, product);
    } else {
      response = await this.apiClient.post(url, product);
    }

    const savedProduct: Product = response.data.data;
    ProductResource.convertProductQualsAndRewardsToClasses(savedProduct);
    return savedProduct;
  }

  public async remove(fiId: number, productId: number): Promise<AxiosResponse> {
    return this.apiClient.delete(`/api/rcx/config/${fiId}/product/${productId}`);
  }

  public async update(fiId: string, product: Product): Promise<Product> {
    const url = `/api/rcx/config/${fiId}/product/${product.id}`;
    const response: AxiosResponse = await this.apiClient.put(url, product);
    const savedProduct: Product = response.data.data;
    ProductResource.convertProductQualsAndRewardsToClasses(savedProduct);
    return savedProduct;
  }

  public async validateProducts(fiId: string): Promise<ProductValidationResult[]> {
    const url = `/api/rcx/config/${fiId}/validate/products`;

    const response: AxiosResponse = await this.apiClient.get(url);
    return response.data.data;
  }

  public async validateQualifier(fiId: string, qualifier: IQualifier): Promise<ValidationResult> {
    const url = `/api/rcx/config/${fiId}/validate/qualifier`;

    const response: AxiosResponse = await this.apiClient.post(url, qualifier);
    return response.data.data;
  }

  public async validateReward(fiId: string, reward: IReward): Promise<ValidationResult> {
    const url = `/api/rcx/config/${fiId}/validate/reward`;
    const response: AxiosResponse = await this.apiClient.post(url, reward);
    return response.data.data;
  }

  public static productHasIssues(validationResult: ProductValidationResult): boolean {
    if (validationResult.rewardResults.length === 0) {
      return true;
    }

    let totalErrors = 0;
    validationResult.rewardResults.forEach((rewardResult: any) => {
      totalErrors += rewardResult.failures.length;
    });

    validationResult.qualifierResults.forEach((rewardResult: any) => {
      totalErrors += rewardResult.failures.length;
    });

    return totalErrors > 0;
  }

  public static createEmptyProduct(): Product {
    return {
      id: null!,
      versionId: null,
      designStartDate: DateTool.todayBackendDate(),
      active: false,
      eocEmailEnabled: false,
      '@type': 'Product',
      productMapping: null!,
      launchDate: null!,
      linkedAccountMethodology: null!,
      kasasaTunesConfiguration: null!,
      serviceFee: false,
      serviceFeeAmount: null!,
      qualRewardGroups: [],
      hasAssociatedResults: false,
    };
  }

  public static createEmptyQualRewardGroup(): QualRewardGroup {
    return {
      level: 0,
      autoQualifyCycles: 0,
      rewards: [],
      qualifiers: [],
      qualificationLogic: 'AND',
    };
  }

  public static deepCopy(original: Product): Product {
    const copy: Product = Object.assign({}, original);
    const rewardGroupsCopy: QualRewardGroup[] = [];
    copy.qualRewardGroups.forEach((group) => {
      const groupCopy: QualRewardGroup = Object.assign({}, group);
      groupCopy.id = undefined;
      groupCopy.qualifiers = ProductResource.cloneQualifiers(group.qualifiers);
      groupCopy.rewards = ProductResource.cloneRewards(group.rewards);
      rewardGroupsCopy.push(groupCopy);
    });
    copy.qualRewardGroups = rewardGroupsCopy;
    return copy;
  }

  private static cloneQualifiers(qualifiers: IQualifier[]): IQualifier[] {
    const clonedQualifiers: IQualifier[] = [];
    qualifiers.forEach((qualfier) => {
      const clonedQualifier = qualfier.clone();
      clonedQualifier.id = undefined;
      ProductResource.clearLoanQualifierIds(clonedQualifier);
      clonedQualifiers.push(clonedQualifier);
    });
    return clonedQualifiers;
  }

  private static cloneRewards(rewards: IReward[]): IReward[] {
    const clonedRewards: IReward[] = [];
    rewards.forEach((reward) => {
      const clonedQualifier = reward.clone();
      clonedQualifier.id = undefined;
      clonedRewards.push(clonedQualifier);
    });
    return clonedRewards;
  }

  private static clearLoanQualifierIds(qualifier: IQualifier) {
    if (qualifier.loanProducts) {
      for (let i = 0; i < qualifier.loanProducts.length; i++) {
        const loanProduct = qualifier.loanProducts[i];
        loanProduct.id = undefined;
      }
    }
    if (qualifier.loanTypes) {
      for (let i = 0; i < qualifier.loanTypes.length; i++) {
        const loanType = qualifier.loanTypes[i];
        loanType.id = undefined;
      }
    }
  }

  private static findMostRecent(productsOrderedByDesignStart: Product[]): Product | null {
    const nowWithTime = new Date();
    const todayNoTime = new Date(nowWithTime.getFullYear(), nowWithTime.getMonth(), nowWithTime.getDate());
    // implementation assumes products are ordered, descending by design start date
    let mostRecent: Product | null = null;
    productsOrderedByDesignStart.forEach((product: Product) => {
      const startDate: Date = DateTool.parseDate(product.designStartDate);
      if (!mostRecent && startDate <= todayNoTime) {
        mostRecent = product;
      }
    });
    if (!mostRecent && productsOrderedByDesignStart.length > 0) {
      mostRecent = productsOrderedByDesignStart[productsOrderedByDesignStart.length - 1];
    }
    return mostRecent;
  }

  private static convertProductQualsAndRewardsToClasses(product: Product) {
    if (product.qualRewardGroups) {
      product.qualRewardGroups.forEach((qualRewardGroup: QualRewardGroup) => {
        ProductResource.convertQualRewardGroupQualsAndRewardsToClasses(qualRewardGroup);
      });
    }
  }

  /* eslint-disable no-param-reassign */
  /* eslint-disable no-underscore-dangle */
  private static convertQualRewardGroupQualsAndRewardsToClasses(qualRewardGroup: QualRewardGroup) {
    for (let i = 0; i < qualRewardGroup.qualifiers.length; i++) {
      qualRewardGroup.qualifiers[i] = ProductResource.convertQualifierToClass(qualRewardGroup.qualifiers[i]);
    }

    for (let i = 0; i < qualRewardGroup.rewards.length; i++) {
      qualRewardGroup.rewards[i] = ProductResource.convertRewardToClass(qualRewardGroup.rewards[i]);
    }
  }

  private static convertQualifierToClass(qualifier: IQualifier): IQualifier {
    if (qualifier._class === IbLoginQualifier.CLASS) {
      return new IbLoginQualifier(qualifier);
    }
    if (qualifier._class === EStatementQualifier.CLASS) {
      return new EStatementQualifier(qualifier);
    }
    if (qualifier._class === AchQualifier.CLASS) {
      return new AchQualifier(qualifier);
    }
    if (qualifier._class === PosQualifier.CLASS) {
      return new PosQualifier(qualifier);
    }
    if (qualifier._class === CreditCardQualifier.CLASS) {
      return new CreditCardQualifier(qualifier);
    }
    if (qualifier._class === CreditOrDebitCardQualifier.CLASS) {
      return new CreditOrDebitCardQualifier(qualifier);
    }
    if (qualifier._class === LinkedAccountQualifier.CLASS) {
      return new LinkedAccountQualifier(qualifier);
    }
    if (qualifier._class === MinimumBalanceQualifier.CLASS) {
      return new MinimumBalanceQualifier(qualifier);
    }
    if (qualifier._class === LoanQualifier.CLASS) {
      return new LoanQualifier(qualifier);
    }
    if (qualifier._class === VafaOptOutQualifier.CLASS) {
      return new VafaOptOutQualifier(qualifier);
    }
    console.error(`Unable to find qualifier class for ${qualifier._class}`);
    return qualifier;
  }

  private static convertRewardToClass(reward: IReward): IReward {
    if (reward._class === AtmReward.CLASS) {
      return new AtmReward(reward);
    } if (reward._class === InterestReward.CLASS) {
      return new InterestReward(reward);
    } if (reward._class === CashBackReward.CLASS) {
      return new CashBackReward(reward);
    } if (reward._class === EatsReward.CLASS) {
      return new EatsReward(reward);
    } if (reward._class === StreamingReward.CLASS) {
      return new StreamingReward(reward);
    } if (reward._class === FeeReductionReward.CLASS) {
      return new FeeReductionReward(reward);
    } if (reward._class === AchAggregateFeeReductionReward.CLASS) {
      return new AchAggregateFeeReductionReward(reward);
    } if (reward._class === PerAchTrxTypeFeeReductionReward.CLASS) {
      return new PerAchTrxTypeFeeReductionReward(reward);
    } if (reward._class === PerPosTrxTypeFeeReductionReward.CLASS) {
      return new PerPosTrxTypeFeeReductionReward(reward);
    } if (reward._class === FlexReward.CLASS) {
      return new FlexReward(reward);
    } if (reward._class === TunesGrantReward.CLASS) {
      return new TunesGrantReward(reward);
    } if (reward._class === IbLoginFeeReductionReward.CLASS) {
      return new IbLoginFeeReductionReward(reward);
    } if (reward._class === EstatementFeeReductionReward.CLASS) {
      return new EstatementFeeReductionReward(reward);
    } if (reward._class === WaiveServiceFeeReward.CLASS) {
      return new WaiveServiceFeeReward(reward);
    } if (reward._class === MinimumBalanceFeeReductionReward.CLASS) {
      return new MinimumBalanceFeeReductionReward(reward);
    }
    console.error(`Unable to find reward class for ${reward._class}`);
    return reward;
  }
  /* eslint-enable no-param-reassign */
  /* eslint-enable no-underscore-dangle */
}
